import { createI18n, FALLBACK_LOCALE, normalizeLocaleCode } from "@iec-id/shared/i18n";

/**
 * Пространство имён на раздел витрины: словарь читается вместе с разделом и не
 * разрастается в один общий файл. Раздел, забытый в этом списке, покажет сырые
 * ключи вместо текста — список и есть единственная точка подключения.
 */
const NAMESPACES = [
	"common",
	"overview",
	"points",
	"plus",
	"services",
	"personal",
	"security",
	"legal",
	"faq",
	"notifications",
	"support",
] as const;

/**
 * Словари подключаются обходом каталога, а не строкой на файл.
 *
 * Локалей 21 (Д11), переводы приезжают отдельной фазой: перечисление импортов
 * означало бы правку этого файла на каждую локаль — то есть словарь, который
 * положили в репозиторий и забыли подключить.
 *
 * Английский едет в основном бандле: он `fallbackLng`, без него интерфейс
 * покажет ключи. Остальные догружаются отдельными чанками по фактическому языку.
 */
type Bundle = Record<string, unknown>;

const eagerEn = import.meta.glob<Bundle>("../locales/en/*.json", {
	eager: true,
	import: "default",
});
// Английский исключён из ленивого набора: он уже в основном бандле, и без
// исключения сборщик честно предупреждает, что «динамический импорт не
// переносит модуль в отдельный чанк» — то есть обещание ленивости было бы
// ложным ровно для той локали, которой оно не нужно.
const lazyAll = import.meta.glob<Bundle>(["../locales/*/*.json", "!../locales/en/*.json"], {
	import: "default",
});

/** `../locales/ru/points.json` → `points`. */
function namespaceOf(path: string): string | undefined {
	return /\/([^/]+)\.json$/.exec(path)?.[1];
}

function bundlesOf(modules: Record<string, Bundle>): Record<string, Bundle> {
	const result: Record<string, Bundle> = {};
	for (const [path, value] of Object.entries(modules)) {
		const ns = namespaceOf(path);
		if (ns) result[ns] = value;
	}
	return result;
}

export const i18n = createI18n({
	resources: { [FALLBACK_LOCALE]: bundlesOf(eagerEn) },
	namespaces: NAMESPACES,
	debug: import.meta.env.DEV,
});

const loaded = new Set<string>([FALLBACK_LOCALE]);
/** Защита от рекурсии: `changeLanguage` сам эмитит `languageChanged`. */
let applying = false;

/**
 * Догрузить словари локали и перерисовать интерфейс.
 *
 * Перерисовка нужна отдельным действием: `react-i18next` по умолчанию слушает
 * только `languageChanged`, а пополнение хранилища (`added`) проходит мимо него.
 */
async function ensureLocale(language: string): Promise<void> {
	const code = normalizeLocaleCode(language);
	if (loaded.has(code)) return;
	loaded.add(code);

	const entries = Object.entries(lazyAll).filter(([path]) => path.includes(`/locales/${code}/`));
	if (entries.length === 0) return;

	await Promise.all(
		entries.map(async ([path, load]) => {
			const ns = namespaceOf(path);
			if (ns) i18n.addResourceBundle(code, ns, await load(), true, true);
		}),
	);

	if (normalizeLocaleCode(i18n.language) !== code) return;
	applying = true;
	try {
		await i18n.changeLanguage(code);
	} finally {
		applying = false;
	}
}

i18n.on("languageChanged", (language: string) => {
	if (!applying) void ensureLocale(language);
});

/**
 * Словарь языка, с которым страница открылась.
 *
 * Приложение ждёт его ПЕРЕД первым рендером (`main.tsx`). Без ожидания
 * интерфейс успевал отрисоваться по-английски — фолбэк есть всегда — и через
 * мгновение переписывался на нужный язык: заголовок, кнопки и подписи менялись
 * у человека на глазах, а вместе с ними прыгала высота блоков.
 *
 * Отказ загрузки не должен оставить человека перед пустым экраном, поэтому
 * промис никогда не отклоняется: не приехал словарь — работаем на английском.
 */
export const localeReady: Promise<void> = ensureLocale(i18n.language).catch(() => undefined);

/**
 * Язык профиля — источник истины для вошедшего человека (В: локаль профиля не
 * подставлялась, и вошедший с чужого браузера видел язык этого браузера).
 *
 * Явный `?lang=` в адресе сильнее: им человек прямо сейчас просит другой язык,
 * и переписывать это решение профилем — значит игнорировать нажатие.
 */
export function applyProfileLocale(locale: string | null | undefined): void {
	if (!locale) return;
	const code = normalizeLocaleCode(locale);
	if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("lang")) {
		return;
	}
	if (normalizeLocaleCode(i18n.language) === code) return;
	void i18n.changeLanguage(code);
}
